import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { FormsModule } from "@angular/forms";
import { BaseChartDirective } from "ng2-charts";
import { ChartConfiguration, ChartType } from "chart.js";
import { UserService } from "../../services/user.service";
import { User } from "../../model/user";
import { AuthenticationService } from "../../services/authentication.service";
import { LocalStorageService } from "../../services/local-storage.service";
import { ClubService } from "../../services/club.service";
import { CourtReservationService } from "../../services/court-reservation.service";

@Component({
  selector: "app-profile",
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective, LucideAngularModule],
  templateUrl: "./profile.component.html",
  styleUrl: "./profile.component.css",
})
export class ProfileComponent implements OnInit {
  id = -1;
  user?: User;
  currentUser: User | null = null;
  clubs: any[] = [];
  isDirty = false;
  isLoading = true;
  isLoadingClubs = true;
  isSaving = false;
  isLoadingGraph = true;
  hasError = false;
  errorMessage = "";

  lineChartData: ChartConfiguration["data"] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: "PP Points Progression",
        fill: false,
        borderColor: "#42c6b8",
        tension: 0.3,
        pointBackgroundColor: "#dfff08",
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };
  lineChartOptions: ChartConfiguration["options"] = { responsive: true };
  lineChartType: ChartType = "line";

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private authService: AuthenticationService,
    private localStorage: LocalStorageService,
    private clubService: ClubService,
    private courtReservationService: CourtReservationService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.currentUserValue;
    this.ensureUserPreferences();

    this.clubService.getClubs().subscribe({
      next: (c) => {
        const currentUsername =
          this.authService.currentUserValue?.username?.toLowerCase();
        this.clubs = c.filter((club: any) => {
          if (
            club.name.toLowerCase().includes("padrinos") &&
            currentUsername !== "dimitrijek"
          ) {
            return false;
          }
          return true;
        });
        this.isLoadingClubs = false;
      },
      error: () => {
        this.isLoadingClubs = false;
      },
    });

    this.route.paramMap.subscribe((params) => {
      const idParam = params.get("id");
      this.id = Number(idParam);

      // Validate the user ID
      if (!idParam || isNaN(this.id) || this.id <= 0) {
        this.hasError = true;
        this.errorMessage = "Invalid user profile. Please try again.";
        this.isLoading = false;
        this.isLoadingGraph = false;
        return;
      }

      this.hasError = false;
      this.errorMessage = "";
      this.loadUser();
    });
  }

  loadUser(): void {
    this.isLoading = true;
    this.userService.getUser(this.id).subscribe({
      next: (u) => {
        this.user = u;
        if (this.id === this.currentUser?.id) {
          this.currentUser = { ...this.currentUser, ...u };
          this.localStorage.setItem("currentUser", this.currentUser);
          this.ensureUserPreferences();
        }
        this.isLoading = false;
        this.loadMatchHistory();
      },
      error: (error) => {
        console.error("Failed to load user data:", error);
        this.hasError = true;
        this.errorMessage =
          "Failed to load profile. Please check your connection and try again.";
        this.isLoading = false;
        this.isLoadingGraph = false;
      },
    });
  }

  loadMatchHistory(): void {
    if (!this.user) return;

    this.isLoadingGraph = true;
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 6);

    this.courtReservationService
      .getCourtReservationsForUser(this.id, startDate, endDate)
      .subscribe({
        next: (reservations) => {
          this.buildGraphData(reservations);
          this.isLoadingGraph = false;
        },
        error: (error) => {
          console.error("Failed to load match history:", error);
          this.buildFallbackGraphData();
          this.isLoadingGraph = false;
        },
      });
  }

  buildGraphData(reservations: any[]): void {
    if (!this.user || !reservations || reservations.length === 0) {
      this.buildFallbackGraphData();
      return;
    }

    const finishedMatches = reservations
      .filter((r) => r.finished && r.reservation_start_time)
      .sort(
        (a, b) =>
          new Date(a.reservation_start_time).getTime() -
          new Date(b.reservation_start_time).getTime(),
      );

    if (finishedMatches.length === 0) {
      this.buildFallbackGraphData();
      return;
    }

    const currentPoints = this.user.pp_points || 0;
    const labels: string[] = [];
    const data: number[] = [];

    const pointsPerMatch = Math.floor(
      currentPoints / Math.max(finishedMatches.length, 1),
    );
    let runningPoints = Math.max(
      0,
      currentPoints - finishedMatches.length * pointsPerMatch,
    );

    finishedMatches.forEach((match, index) => {
      const matchDate = new Date(match.reservation_start_time);
      const label = matchDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

      runningPoints += pointsPerMatch;

      if (
        index === 0 ||
        index % Math.ceil(finishedMatches.length / 10) === 0 ||
        index === finishedMatches.length - 1
      ) {
        labels.push(label);
        data.push(runningPoints);
      }
    });

    if (data[data.length - 1] !== currentPoints) {
      data[data.length - 1] = currentPoints;
    }

    this.lineChartData = {
      labels,
      datasets: [
        {
          data,
          label: "PP Points Progression",
          fill: false,
          borderColor: "#42c6b8",
          tension: 0.3,
          pointBackgroundColor: "#dfff08",
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };
  }

  buildFallbackGraphData(): void {
    const currentPoints = this.user?.pp_points || 0;
    const labels: string[] = [];
    const data: number[] = [];

    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now);
      date.setMonth(date.getMonth() - i);
      labels.push(date.toLocaleDateString("en-US", { month: "short" }));

      const progress = (6 - i) / 6;
      data.push(Math.floor(currentPoints * progress));
    }

    this.lineChartData = {
      labels,
      datasets: [
        {
          data,
          label: "PP Points Progression",
          fill: false,
          borderColor: "#42c6b8",
          tension: 0.3,
          pointBackgroundColor: "#dfff08",
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };
  }

  toggleAvailability(): void {
    if (this.currentUser) {
      this.currentUser.availableToPlay = !this.currentUser.availableToPlay;
      this.isDirty = true;
    }
  }

  onSelectionChange(): void {
    if (this.currentUser) {
      this.isDirty = true;
    }
  }

  private async ensureUserPreferences(): Promise<void> {
    if (!this.currentUser) return;

    const savedPreferences = await this.localStorage.getItem("userPreferences");
    if (savedPreferences) {
      if (savedPreferences.userId === this.currentUser.id) {
        this.currentUser.favouriteHand =
          savedPreferences.favouriteHand || "right";
        this.currentUser.favouriteMatch =
          savedPreferences.favouriteMatch || "competitive";
        this.currentUser.club_id =
          savedPreferences.club_id || this.currentUser.club_id;
        this.currentUser.availableToPlay =
          savedPreferences.availableToPlay ?? true;
      }
    }

    if (!this.currentUser.favouriteHand) {
      this.currentUser.favouriteHand = "right";
    }
    if (!this.currentUser.favouriteMatch) {
      this.currentUser.favouriteMatch = "competitive";
    }
  }

  private async saveUserPreferences(): Promise<void> {
    if (!this.currentUser) return;

    const preferences = {
      userId: this.currentUser.id,
      favouriteHand: this.currentUser.favouriteHand,
      favouriteMatch: this.currentUser.favouriteMatch,
      club_id: this.currentUser.club_id,
      availableToPlay: this.currentUser.availableToPlay,
    };

    await this.localStorage.setItem("userPreferences", preferences);
  }

  getWinRate(): number {
    if (!this.user || !this.user.wins || !this.user.loses) return 0;
    const totalMatches = this.user.wins + this.user.loses;
    if (totalMatches === 0) return 0;
    return Math.round((this.user.wins / totalMatches) * 100);
  }

  getLeague(): string {
    const points = this.user?.pp_points || 0;

    if (points >= 1000) return "League 1";
    if (points >= 750) return "League 2";
    if (points >= 500) return "League 3";
    if (points >= 250) return "League 4";
    return "League 5";
  }

  saveChanges(): void {
    if (!this.currentUser) return;

    this.isSaving = true;
    this.userService.getUser(this.currentUser.id).subscribe({
      next: (latestUserData) => {
        const payload = {
          id: this.currentUser!.id,
          username: latestUserData.username || this.currentUser!.username,
          name: latestUserData.name || this.currentUser!.name,
          last_name: latestUserData.last_name || this.currentUser!.last_name,
          email:
            latestUserData.email ||
            this.currentUser!.email ||
            this.currentUser!.mail,
          phone_number:
            latestUserData.phone_number || this.currentUser!.phone_number,
          gender:
            latestUserData.gender !== undefined
              ? latestUserData.gender
              : this.currentUser!.gender,
          pp_points: latestUserData.pp_points || this.currentUser!.pp_points,
          wins: latestUserData.wins || this.currentUser!.wins,
          loses: latestUserData.loses || this.currentUser!.loses,
          points: latestUserData.points || this.currentUser!.points,
          ranking: latestUserData.ranking || this.currentUser!.ranking,
          available_to_play: this.currentUser!.availableToPlay,
          profile_image:
            latestUserData.profile_image || this.currentUser!.profile_image,
          club_id: this.currentUser!.club_id,
          lockoutCounter: latestUserData.lockoutCounter || 0,
          userStatusId: latestUserData.userStatusId || 1,
          lastInvLoginAttempt: latestUserData.lastInvLoginAttempt,
          email_verified: latestUserData.email_verified,
          verified_as_vip_date: latestUserData.verified_as_vip_date,
          verified_as_vip_note: latestUserData.verified_as_vip_note,
          roles: latestUserData.roles || this.currentUser!.roles || [],
        };

        console.log("Sending payload:", payload);

        this.userService.updateUserWithBody(payload).subscribe({
          next: async () => {
            await this.saveUserPreferences();
            sessionStorage.setItem(
              "currentUser",
              JSON.stringify(this.currentUser),
            );
            if (this.currentUser) {
              await this.authService.updateCurrentUser(this.currentUser);
            }
            this.loadUser();
            this.isDirty = false;
            this.isSaving = false;
          },
          error: (error) => {
            console.error("Save error:", error);
            this.isDirty = false;
            this.isSaving = false;
          },
        });
      },
      error: (error) => {
        console.error("Error fetching latest user data:", error);
        this.isDirty = false;
        this.isSaving = false;
      },
    });
  }
}
